import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAppSelector } from 'hooks/reduxHooks'
import { requestClassicRewardAvailability } from 'api/loyalty/requestClassicRewardAvailability'
import type { ClassicRewardAvailabilityResult } from 'api/loyalty/requestClassicRewardAvailability'
import { split } from 'lib/array/arrayUtils'

// svc-lux-loyalty /rewards/availability request fields
const ITEM_TYPE = 'flight'
const ITEM_SUB_TYPE = 'charter'
const MAX_ITEMS_PER_REQUEST = 20
/** svc-lux-loyalty entries don't change often per `journey_key`; tune up/down as backend cache TTL evolves. */
const STALE_TIME_MS = 5 * 60 * 1000

export interface CharterRewardLeg {
  /** Full `journey_key` (encodes cabin + fare family). */
  journeyKey: string
  /** Total cash price for this leg across all paying passengers. */
  legTotal: number
  /** Optional caller metadata passed through to the priced leg result. */
  itemId?: string
}

export interface CharterRewardLegResult {
  journeyKey: string
  itemId?: string
  available: boolean
  entryId?: string
  /** Per-passenger points required. */
  pointsPrice?: number
  /** Per-passenger cash co-payment. */
  cashPrice?: number
  cashCurrency?: string
  /**
   * Per-passenger value of the points portion in the local currency:
   * `(legTotal - cashPrice * passengerCount) / passengerCount`.
   */
  pointsValue?: number
  remainingInventory?: number
}

export interface UseCharterRewardAvailabilityResult {
  isLoading: boolean
  /** All legs available with inventory. */
  available: boolean
  legs: Array<CharterRewardLegResult>
  /** Sum of `pointsPrice` across all legs for a single passenger (for `/person` display). */
  pointsPerPerson?: number
  /** Sum of `cashPrice` across all legs for a single passenger (for `/person` display). */
  cashCoPayPerPerson?: number
}

interface UseCharterRewardAvailabilityArgs {
  legs: Array<CharterRewardLeg>
  /** Adults + children (infants fly free on charter and are excluded). */
  passengerCount: number
  disabled?: boolean
  /** When true, always re-fetch on mount instead of serving stale cache. Use for checkout entry. */
  refetchOnMount?: boolean | 'always'
}

const EMPTY_RESULT: UseCharterRewardAvailabilityResult = {
  isLoading: false,
  available: false,
  legs: [],
}

function isBadRequest(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === 400
}

/**
 * Looks up Classic Rewards availability for a charter bundle's legs.
 *
 * Calls svc-lux-loyalty `POST /api/lux-loyalty/rewards/availability` with one item per
 * `(leg × cabin variant)` keyed by `journey_key`, chunked by 20. Cached by React Query
 * keyed on `(customerId, currency, sortedJourneyKeys)` so duplicate lookups across
 * surfaces (modal, calendar, tile) hit the cache instead of re-firing.
 *
 * Treats reward fares as all-or-nothing across legs — if any leg is unavailable,
 * `available` is `false` and aggregate totals are undefined. Computes per-leg
 * `pointsValue` locally from `legTotal` and `cashPrice`.
 */
export default function useCharterRewardAvailability({
  legs,
  passengerCount,
  disabled = false,
  refetchOnMount,
}: UseCharterRewardAvailabilityArgs): UseCharterRewardAvailabilityResult {
  const queryClient = useQueryClient()
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')
  const currency = useAppSelector(state => state.geo.currentCurrency)

  const sortedJourneyKeys = useMemo(
    () => legs.map(leg => leg.journeyKey).sort(),
    [legs],
  )

  const enabled =
    !disabled && legs.length > 0 && passengerCount > 0 && !!customerId && !!currency
  const queryKey = useMemo(
    () => ['classicRewardAvailability', customerId, currency, sortedJourneyKeys],
    [customerId, currency, sortedJourneyKeys],
  )
  const previousQuery = queryClient.getQueryState(queryKey)
  const recentBadRequest = isBadRequest(previousQuery?.error) &&
    Date.now() - (previousQuery?.errorUpdatedAt ?? 0) < STALE_TIME_MS

  const { data: results, isLoading, isFetching } = useQuery<Array<ClassicRewardAvailabilityResult>>({
    queryKey,
    queryFn: async() => {
      const items = legs.map(leg => ({
        itemType: ITEM_TYPE,
        itemSubType: ITEM_SUB_TYPE,
        itemReference: leg.journeyKey,
        itemSubReference: '',
      }))
      const chunks = split(items, MAX_ITEMS_PER_REQUEST)
      const chunkResults = await Promise.all(
        chunks.map(chunkItems => requestClassicRewardAvailability(customerId, currency, chunkItems)),
      )
      return chunkResults.flat()
    },
    enabled,
    staleTime: STALE_TIME_MS,
    // A 400 with unchanged inputs will fail again. Guard both error-only
    // queries and queries that still hold data from an earlier success.
    ...(recentBadRequest
      ? { retryOnMount: false, refetchOnMount: false }
      : refetchOnMount ? { refetchOnMount } : {}),
  })

  return useMemo<UseCharterRewardAvailabilityResult>(() => {
    if (!enabled) return EMPTY_RESULT
    if (!results) return { ...EMPTY_RESULT, isLoading: isLoading || isFetching }

    const resultByKey = new Map(results.map(item => [item.itemReference, item]))

    const enrichedLegs: Array<CharterRewardLegResult> = legs.map(leg => {
      const result = resultByKey.get(leg.journeyKey)
      if (!result?.available) {
        return { journeyKey: leg.journeyKey, itemId: leg.itemId, available: false }
      }
      const cashPrice = result.cashPrice
      const pointsValue = cashPrice !== undefined && passengerCount > 0
        ? (leg.legTotal - cashPrice * passengerCount) / passengerCount
        : undefined
      return {
        journeyKey: leg.journeyKey,
        itemId: leg.itemId,
        available: true,
        entryId: result.entryId,
        pointsPrice: result.pointsPrice,
        cashPrice,
        cashCurrency: result.cashCurrency,
        pointsValue,
        remainingInventory: result.remainingInventory,
      }
    })

    const allAvailable = enrichedLegs.every(leg => leg.available)
    if (!allAvailable) {
      return { isLoading: false, available: false, legs: enrichedLegs }
    }

    const pointsPerPerson = enrichedLegs.reduce(
      (sum, leg) => sum + (leg.pointsPrice ?? 0),
      0,
    )
    const cashCoPayPerPerson = enrichedLegs.reduce(
      (sum, leg) => sum + (leg.cashPrice ?? 0),
      0,
    )

    return {
      isLoading: false,
      available: true,
      legs: enrichedLegs,
      pointsPerPerson,
      cashCoPayPerPerson,
    }
  }, [enabled, results, isLoading, isFetching, legs, passengerCount])
}
